import { Component, OnInit, OnChanges, SimpleChanges, inject, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, ValidationErrors } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { distinctUntilChanged, switchMap, debounceTime } from 'rxjs/operators';
import { of } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../shared/material/material.module';
import { City, CityService } from '../../shared/services/city.service';
import { State, StateService } from '../../shared/services/state.service';
import { data_city_geo } from '../../shared/data/data_city_geo';
import { ResourceExamesEnum, ResourceTerapiaEnum } from '../../shared/enums';
import { AvailabilityComponent } from '../../shared/availability/availability.component';
import { SearchResourceControllerService } from '../../core/generated/api/searchResourceController.service';
import { PagedModel } from '../../core/generated/model/pagedModel';
import { SearchResourceRequest } from '../../core/generated/model/searchResourceRequest';
import { PageEvent } from '@angular/material/paginator';
import { AdvancedSearchResourceResultsComponent } from '../advanced-search-resource-results/advanced-search-resource-results.component';
import { ActiveFiltersCounterComponent } from '../../shared/active-filters-counter/active-filters-counter.component';
import { CityChipsSelectorComponent } from '../../shared/city-chips-selector/city-chips-selector.component';
import { ActiveFilter } from '../models/search-state.models';
import { SearchCacheService } from '../../shared/services/search-cache.service';
import { accordionAnimation, fadeIn, expandCollapse } from '../shared/animations';

interface DataCityGeo {
  ibgeId: number;
  nome: string;
  latitude: number;
  longitude: number;
  mesorregiaoNome: string;
  UFSigla: string;
  UFNome: string;
}

interface ResourceOption {
  key: string;
  label: string;
}

/**
 * AdvancedSearchResourceFormComponent
 * 
 * Formulário de busca avançada para recursos médicos (exames/terapias).
 * 
 * Requisitos: 11.2 - Animar expansão/colapso de filtros
 * 
 * @example
 * ```html
 * <app-advanced-search-resource-form
 *   [searchConfig]="{ type: 'OFFERING', cpfCnpj: '12345678901' }">
 * </app-advanced-search-resource-form>
 * ```
 */
@Component({
    selector: 'app-advanced-search-resource-form',
  standalone: true,
    templateUrl: './advanced-search-resource-form.component.html',
    styleUrls: ['./advanced-search-resource-form.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MaterialModule,
        AvailabilityComponent,
        AdvancedSearchResourceResultsComponent,
        ActiveFiltersCounterComponent,
        CityChipsSelectorComponent
    ],
    animations: [accordionAnimation, fadeIn, expandCollapse]
})
export class AdvancedSearchResourceFormComponent implements OnInit, OnChanges {
  @Input() searchConfig: { type?: 'OFFERING' | 'DEMAND'; cpfCnpj?: string } | null = null;

  // Services
  private readonly advancedSearchService = inject(SearchResourceControllerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly cityService = inject(CityService);
  private readonly stateService = inject(StateService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly searchCacheService = inject(SearchCacheService);

  // Form and data
  searchForm!: FormGroup;
  states: State[] = [];
  cities: DataCityGeo[] = [];
  nearbyCities: City[] = [];
  selectedCities: City[] = [];

  // UI state
  isLoading = false;
  isMobile = false;
  showActivityAreas = false;
  private _editingMode = false;
  showResourceSelection = false;
  isFromCache = false; // Indica se os resultados vieram do cache

  // Pagination
  pageSize = 10;
  pageIndex = 0;
  resultTest: PagedModel | null = null;

  // Active filters
  activeFilters: ActiveFilter[] = [];

  // Resource options separated by category
  examResources: ResourceOption[] = [];
  therapyResources: ResourceOption[] = [];

  get editingMode(): boolean {
    return this._editingMode;
  }

  set editingMode(value: boolean) {
    this._editingMode = value;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.initForm();
    this.initializeResourceOptions();
    this.loadStates();
    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions(): void {
    // Compute active filters on any form change
    this.searchForm.valueChanges.subscribe(() => {
      this.computeActiveFilters();
    });

    // Listen to resource category changes
    this.searchForm.get('resourceCategory')?.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe(category => {
      this.resetResourceSelection();
      this.showResourceSelection = !!category;
    });

    // Listen to state changes to load corresponding cities
    this.searchForm.get('state')?.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap(stateCode => {
        this.searchForm.get('referenceCity')?.setValue('');
        this.cities = [];

        if (stateCode) {
          // Verifica cache de cidades por estado
          const citiesCacheKey = `cities_${stateCode}`;
          const cachedCities = this.searchCacheService.get<DataCityGeo[]>(citiesCacheKey);
          
          if (cachedCities) {
            return of(cachedCities);
          }
          
          this.isLoading = true;
          const cities = data_city_geo.filter(city => city.UFSigla === stateCode) as DataCityGeo[];
          
          // Armazena no cache (TTL de 1 hora para cidades)
          this.searchCacheService.set(citiesCacheKey, cities, 60 * 60 * 1000);
          
          return of(cities);
        }
        return of([]);
      })
    ).subscribe(cities => {
      this.cities = cities;
      this.isLoading = false;

      const referenceCityControl = this.searchForm.get('referenceCity');
      if (cities?.length > 0) {
        referenceCityControl?.enable({ emitEvent: false });
      } else {
        referenceCityControl?.disable({ emitEvent: false });
        referenceCityControl?.setValue('');
      }
    });

    // Update nearby cities when reference city or radius changes with debounce
    this.searchForm.get('referenceCity')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.searchForm.get('radiusInKm')?.value > 0) {
        this.updateNearbyCities();
      }
    });

    this.searchForm.get('radiusInKm')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.updateNearbyCities();
    });

    // Date validation with debounce
    this.searchForm.get('publicationDateFrom')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.searchForm.updateValueAndValidity({ emitEvent: false });
    });

    this.searchForm.get('publicationDateTo')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.searchForm.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchConfig'] && this.searchForm) {
      const cfg = changes['searchConfig'].currentValue as { type?: string; cnpj?: string } | null;
      if (cfg && cfg.cnpj) {
        const control = this.searchForm.get('searcherCpfCnpj');
        if (control) {
          control.setValue(cfg.cnpj);
        }
      }
    }
  }

  private initializeResourceOptions(): void {
    // Create options for exams
    this.examResources = Object.entries(ResourceExamesEnum).map(([key, value]) => ({
      key: key,
      label: value
    })).sort((a, b) => a.label.localeCompare(b.label));

    // Create options for therapies
    this.therapyResources = Object.entries(ResourceTerapiaEnum).map(([key, value]) => ({
      key: key,
      label: value
    })).sort((a, b) => a.label.localeCompare(b.label));


  }

  initForm(): void {
    this.searchForm = this.fb.group({
      searcherCpfCnpj: [''],
      resourceCategory: [''],
      resourceTypes: this.fb.array([]),
      state: [''],
      referenceCity: [{ value: '', disabled: true }],
      radiusInKm: [0, [Validators.min(0), Validators.max(80)]],
      publicationDateFrom: [''],
      publicationDateTo: [''],
      availability: this.fb.array(this.getAllSlots()),
    }, { validators: this.validateAmountRange });
  }


  private loadStates(): void {
    this.stateService.getStates().subscribe(states => {
      this.states = states;
    });
  }



  // Retorna todos os slots possíveis (diaId 2..8, periodId 1..3) como números concatenados
  private getAllSlots(): number[] {
    const slots: number[] = [];
    for (let dayId = 2; dayId <= 8; dayId++) {
      for (let periodId = 1; periodId <= 3; periodId++) {
        slots.push(parseInt(`${dayId}${periodId}`));
      }
    }
    return slots;
  }

  get resourceTypesArray(): FormArray {
    return this.searchForm.get('resourceTypes') as FormArray;
  }

  getResourceOptions(): ResourceOption[] {
    const category = this.searchForm.get('resourceCategory')?.value;
    if (category === 'exames') {
      return this.examResources;
    } else if (category === 'terapias') {
      return this.therapyResources;
    }
    return [];
  }

  resetResourceSelection(): void {
    const resourceTypesArray = this.resourceTypesArray;
    while (resourceTypesArray.length > 0) {
      resourceTypesArray.removeAt(0);
    }

    // Add checkboxes for the selected category
    const options = this.getResourceOptions();
    options.forEach(() => {
      resourceTypesArray.push(this.fb.control(false));
    });
  }

  onResourceCheckboxChange(index: number, checked: boolean): void {
    const control = this.resourceTypesArray.at(index);
    control.setValue(checked);
  }

  validateAmountRange(group: FormGroup) {
    const min = group.get('amountMin')?.value ?? null;
    const max = group.get('amountMax')?.value ?? null;

    if (min !== null && max !== null && min > max) {
      return { amountRangeInvalid: true };
    }
    return null;
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(controlName: string): string {
    const control = this.searchForm.get(controlName);
    if (!control || !control.errors) return '';

    if (control.hasError('required')) {
      return 'Este campo é obrigatório';
    }
    if (control.hasError('maxLength')) {
      const maxLength = control.errors['maxLength']?.requiredLength;
      return `Máximo de ${maxLength} caracteres`;
    }
    if (control.hasError('min')) {
      const min = control.errors['min']?.min;
      return `Valor mínimo: ${min}`;
    }
    if (control.hasError('max')) {
      const max = control.errors['max']?.max;
      return `Valor máximo: ${max}`;
    }
    return '';
  }

  /**
   * Check if a control has been touched and is invalid
   */
  showError(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Check if a control is valid and has value
   */
  showSuccess(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    return !!(control && control.valid && control.value && (control.dirty || control.touched));
  }

  updateNearbyCities(): void {
    const selectedCity = this.searchForm.get('referenceCity')?.value;
    const radiusInKm = this.searchForm.get('radiusInKm')?.value;

    if (selectedCity && radiusInKm > 0) {
      const cityObj = this.cities.find(city => city.nome === selectedCity);

      if (cityObj) {
        this.isLoading = true;
        this.nearbyCities = this.cityService.findCitiesInRadius(cityObj, radiusInKm);

        this.selectedCities = this.selectedCities.filter(selected =>
          this.nearbyCities.some(nearby =>
            nearby.name === selected.name && nearby.state === selected.state
          )
        );
      }
      this.isLoading = false;
    } else {
      this.nearbyCities = [];
    }
  }

  /**
   * Manipula mudanças na seleção de cidades do CityChipsSelectorComponent
   */
  onCitySelectionChange(cities: City[]): void {
    this.selectedCities = cities;
  }

  submitSearch(): void {
    this.editingMode = false;
    if (this.searchForm.valid && this.canSearch()) {
      const formValue = this.searchForm.value;

      // Get selected resource types
      const selectedResourceTypes: Array<SearchResourceRequest.ResourceEnum> = [];
      const resourceOptions = this.getResourceOptions();
      const resourceTypesValues = formValue.resourceTypes || [];

      resourceTypesValues.forEach((selected: boolean, index: number) => {
        if (selected && resourceOptions[index]) {
          selectedResourceTypes.push(resourceOptions[index].key as SearchResourceRequest.ResourceEnum);
        }
      });

      const searchRequest: SearchResourceRequest = {
        resource: selectedResourceTypes.length > 0 ? selectedResourceTypes : undefined,
        state: formValue.state,
        referenceCity: this.selectedCities.length > 0 
          ? [...this.selectedCities.map(city => city.name), formValue.referenceCity].filter(Boolean)
          : (formValue.referenceCity ? [formValue.referenceCity] : undefined),
        availability: formValue.availability,
        publicationDateFrom: formValue.publicationDateFrom,
        publicationDateTo: formValue.publicationDateTo,
        page: 0,
        size: this.pageSize,
        sortBy: 'publicationDate',
        sortDirection: 'DESC',
      };

      this.pageIndex = 0;
      this.loadSearch(this.pageIndex, this.pageSize, searchRequest);
    }
  }

  loadSearch(pageIndex: number = 0, pageSize: number = 10, overrideRequest?: any, bypassCache: boolean = false) {
    const formValue = this.searchForm.value;
    const cities = this.selectedCities.map(city => `${city.name}`);

    const selectedResources = this.resourceTypesArray.controls
      .map((control, index) => control.value ? this.getResourceOptions()[index].key : null)
      .filter((key: string | null) => key !== null);

    const searchRequest: SearchResourceRequest = {
      resource: overrideRequest?.resourceType || (selectedResources.length > 0 ? selectedResources : undefined),
      state: formValue.state,
      referenceCity: cities.length ? [...cities, formValue.referenceCity].filter(Boolean) : (formValue.referenceCity ? [formValue.referenceCity] : undefined),
      availability: formValue.availability,
      publicationDateFrom: overrideRequest?.publicationDateFrom || formValue.publicationDateFrom,
      publicationDateTo: overrideRequest?.publicationDateTo || formValue.publicationDateTo,
      page: pageIndex,
      size: pageSize,
      sortBy: overrideRequest?.sortBy || 'publicationDate',
      sortDirection: overrideRequest?.sortDirection || 'DESC',
    };

    // Gera chave de cache baseada nos parâmetros de busca
    const cacheKey = this.searchCacheService.generateKey({
      ...searchRequest,
      searchType: this.searchConfig?.type
    });

    // Verifica cache se não for bypass
    if (!bypassCache) {
      const cachedResult = this.searchCacheService.get<PagedModel>(cacheKey);
      if (cachedResult) {
        this.resultTest = cachedResult;
        this.isFromCache = true;
        this.cdr.markForCheck();
        return;
      }
    }

    this.isLoading = true;
    this.isFromCache = false;

    const searchObservable = this.searchConfig?.type === 'DEMAND'
      ? this.advancedSearchService.searchDemand1(searchRequest)
      : this.advancedSearchService.searchOffering1(searchRequest);

    searchObservable.subscribe({
      next: (response: PagedModel | null) => {
        this.resultTest = response as PagedModel;
        
        // Armazena no cache
        if (response) {
          this.searchCacheService.set(cacheKey, response);
        }
        
        this.cdr.markForCheck();
      },
      error: () => {
        this.resultTest = null;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onPageChange(event: PageEvent | any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadSearch(this.pageIndex, this.pageSize);
  }

  /**
   * Atualiza os resultados ignorando o cache
   */
  refreshSearch(): void {
    this.loadSearch(this.pageIndex, this.pageSize, undefined, true);
  }

  async resetForm(): Promise<void> {
    this.searchForm.reset({
      radiusInKm: 0,
    });
    // Ensure availability is reset to include all slots
    this.searchForm.setControl('availability', this.fb.array(this.getAllSlots()));

    this.selectedCities = [];
    this.nearbyCities = [];
    this.cities = [];
    this.showResourceSelection = false;
  }

  canSearch(): boolean {
    const formValues = this.searchForm.value;
    return Object.keys(formValues).some(key => {
      const value = formValues[key];
      if (typeof value === 'boolean') {
        return value === true;
      }
      if (Array.isArray(value)) {
        return value.some(v => v === true);
      }
      return value !== null && value !== '' && value !== 0;
    }) || this.selectedCities.length > 0;
  }

  get availability() {
    return this.searchForm.get('availability') as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray) {
    this.searchForm.setControl('availability', newAvailability);
  }

  editSearch(): void {
    this.editingMode = true;
    this.cdr.markForCheck();
  }

  newSearch(): void {
    this.resetForm();
    this.resultTest = null;
    this.editingMode = false;
    this.pageIndex = 0;
    this.pageSize = 10;
    this.cdr.markForCheck();
  }

  /**
   * Computa os filtros ativos do formulário
   */
  computeActiveFilters(): void {
    const filters: ActiveFilter[] = [];
    const formValue = this.searchForm.value;

    // Categoria de Recursos
    if (formValue.resourceCategory) {
      const categoryLabel = formValue.resourceCategory === 'exames' ? 'Exames' : 'Terapias';
      filters.push({
        category: 'Recursos',
        label: 'Categoria',
        value: categoryLabel,
        fieldName: 'resourceCategory'
      });
    }

    // Tipos de Recursos Selecionados
    const selectedResourceTypes: string[] = [];
    const resourceOptions = this.getResourceOptions();
    const resourceTypesValues = formValue.resourceTypes || [];

    resourceTypesValues.forEach((selected: boolean, index: number) => {
      if (selected && resourceOptions[index]) {
        selectedResourceTypes.push(resourceOptions[index].label);
      }
    });

    if (selectedResourceTypes.length > 0) {
      filters.push({
        category: 'Recursos',
        label: 'Tipos Selecionados',
        value: `${selectedResourceTypes.length} tipo(s)`,
        fieldName: 'resourceTypes'
      });
    }

    // Datas de Publicação
    if (formValue.publicationDateFrom) {
      const dateStr = new Date(formValue.publicationDateFrom).toLocaleDateString('pt-BR');
      filters.push({
        category: 'Recursos',
        label: 'Publicado a partir de',
        value: dateStr,
        fieldName: 'publicationDateFrom'
      });
    }

    if (formValue.publicationDateTo) {
      const dateStr = new Date(formValue.publicationDateTo).toLocaleDateString('pt-BR');
      filters.push({
        category: 'Recursos',
        label: 'Publicado até',
        value: dateStr,
        fieldName: 'publicationDateTo'
      });
    }

    // Localização
    if (formValue.state) {
      const stateName = this.states.find(s => s.code === formValue.state)?.name || formValue.state;
      filters.push({
        category: 'Localização',
        label: 'Estado',
        value: stateName,
        fieldName: 'state'
      });
    }

    if (formValue.referenceCity) {
      filters.push({
        category: 'Localização',
        label: 'Cidade de Referência',
        value: formValue.referenceCity,
        fieldName: 'referenceCity'
      });
    }

    if (formValue.radiusInKm > 0) {
      filters.push({
        category: 'Localização',
        label: 'Raio',
        value: `${formValue.radiusInKm} km`,
        fieldName: 'radiusInKm'
      });
    }

    if (this.selectedCities.length > 0) {
      filters.push({
        category: 'Localização',
        label: 'Cidades Selecionadas',
        value: `${this.selectedCities.length} cidade(s)`,
        fieldName: 'selectedCities'
      });
    }

    // Disponibilidade
    const availabilityCount = formValue.availability?.filter((v: number) => v).length || 0;
    if (availabilityCount > 0 && availabilityCount < 21) {
      filters.push({
        category: 'Detalhes',
        label: 'Disponibilidade',
        value: `${availabilityCount} período(s)`,
        fieldName: 'availability'
      });
    }

    this.activeFilters = filters;
  }

  /**
   * Remove um filtro específico
   */
  onFilterRemoved(filter: ActiveFilter): void {
    const control = this.searchForm.get(filter.fieldName);
    
    if (control) {
      // Handle different field types
      if (filter.fieldName === 'resourceTypes') {
        const resourceTypesArray = this.resourceTypesArray;
        for (let i = 0; i < resourceTypesArray.length; i++) {
          resourceTypesArray.at(i).setValue(false);
        }
      } else if (filter.fieldName === 'selectedCities') {
        this.selectedCities = [];
      } else if (typeof control.value === 'boolean') {
        control.setValue(false);
      } else if (typeof control.value === 'number') {
        control.setValue(filter.fieldName === 'radiusInKm' ? 0 : null);
      } else {
        control.setValue('');
      }
    }

    this.computeActiveFilters();
  }

  /**
   * Navega para o campo do filtro
   */
  onFilterClicked(fieldName: string): void {
    const element = document.querySelector(`[formControlName="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Focus the element if it's an input
      if (element instanceof HTMLElement) {
        setTimeout(() => element.focus(), 300);
      }
    }
  }

  /**
   * Limpa todos os filtros
   */
  onClearAllFilters(): void {
    this.resetForm();
    this.computeActiveFilters();
  }
}